import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { apiEndpoint } from 'utility/Utils'

const initialState = {
  tag: {
    size: 10,
    page: 0,
    sortby: undefined,
    sortdir: undefined,
    s: undefined,
  },
  isOpenModal: false,
  sellerFillData: [],
  sellerFillLoading: false,
  sellerFillError: null,
  summaryData: null,
  adjustmentInvoice: null,
  successMessage: null,
  loading: false,
  error: null,
}

export const fetchSellerFills = createAsyncThunk(
  'reportFulfilment/fetchSellerFills',
  async (
    {
      page = 0,
      size = 10,
      sortby = 'invId',
      sortdir = 'ASC',
      escrowpaymentId,
      search,
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const { auth } = getState()

      const params = {
        page,
        size,
        sortby,
        sortdir,
        ...(escrowpaymentId && { escrowpaymentId }),
        ...(search && { search }),
      }

      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        params,
      }

      const response = await axios.get(
        `${apiEndpoint}/api/v1/bussines/sellerfills/list`,
        config,
      )

      return response.data
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch seller fills',
      )
    }
  },
)

export const fetchSellerFillsSummary = createAsyncThunk(
  'sellerFill/fetchSummary',
  async (
    { escrowpaymentId, page = 0, size = 10, sortby = 'unit', sortdir = 'ASC' },
    { rejectWithValue, getState },
  ) => {
    try {
      const { auth } = getState()
      const config = {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          'Content-Type': 'application/json',
        },
        params: {
          page,
          size,
          sortby,
          sortdir,
          escrowpaymentId,
        },
      }

      const response = await axios.get(
        `${apiEndpoint}/api/v1/bussines/sellerfills/summary`,
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)

export const fetchAdjustmentInvoice = createAsyncThunk(
  'sellerFill/fetchAdjustmentInvoice',
  async (escrowpaymentId, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
      }
      const response = await axios.get(
        `${apiEndpoint}/api/v1/bussines/transaction/adjustment/invoice?escrowpaymentId=${escrowpaymentId}`,
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)

export const setBatchInventory = createAsyncThunk(
  'sellerFill/setDipesan',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
      }
      const response = await axios.post(
        `${apiEndpoint}/api/v1/bussines/sellerfills/setdipesan`,
        payload,
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)

export const unsetInventory = createAsyncThunk(
  'sellerFill/batalDipesan',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
      }
      const response = await axios.post(
        `${apiEndpoint}/api/v1/bussines/sellerfills/bataldipesan`,
        payload,
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)

const reportFulfilmentSlice = createSlice({
  name: 'reportFulfilment',
  initialState,
  reducers: {
    setFetchOption(state, action) {
      state.tag = { ...state.tag, ...action.payload }
    },
    toggleModal(state) {
      state.isOpenModal = !state.isOpenModal
    },
    clearReportFulfilmentState(state) {
      state.summaryData = null
      state.adjustmentInvoice = null
      state.successMessage = null
      state.error = null
      state.loading = false
      state.sellerFillLoading = false
      state.sellerFillError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSellerFills.pending, (state) => {
        state.sellerFillLoading = true
        state.sellerFillError = null
      })
      .addCase(fetchSellerFills.fulfilled, (state, action) => {
        state.sellerFillLoading = false
        state.sellerFillData = action.payload?.items || []
      })
      .addCase(fetchSellerFills.rejected, (state, action) => {
        state.sellerFillLoading = false
        state.sellerFillError = action.payload
      })

      .addCase(fetchSellerFillsSummary.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSellerFillsSummary.fulfilled, (state, action) => {
        state.loading = false
        state.summaryData = action.payload
      })
      .addCase(fetchSellerFillsSummary.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(fetchAdjustmentInvoice.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdjustmentInvoice.fulfilled, (state, action) => {
        state.loading = false
        state.adjustmentInvoice = action.payload
      })
      .addCase(fetchAdjustmentInvoice.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(setBatchInventory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(setBatchInventory.fulfilled, (state, action) => {
        state.loading = false
        state.successMessage = 'Berhasil dipesan'
      })
      .addCase(setBatchInventory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(unsetInventory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(unsetInventory.fulfilled, (state, action) => {
        state.loading = false
        state.successMessage = 'Berhasil batal dipesan'
      })
      .addCase(unsetInventory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { setFetchOption, toggleModal, clearReportFulfilmentState } =
  reportFulfilmentSlice.actions

export default reportFulfilmentSlice.reducer
